import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useSittingStore from '../../stores/sittingStore';
import { useTheme } from '../../theme/ThemeContext';
import useSittingActions from '../../hooks/useSittingActions';
import useCoarsePointer from '../../hooks/useCoarsePointer';
import usePortraitOrientation from '../../hooks/usePortraitOrientation';
import useRailState from '../rail/useRailState';
import useProcedurePrefillBridge from '../rail/useProcedurePrefillBridge';
import ProcedureChipList from '../../components/procedures/widget/ProcedureChipList';
import OdontogramSection from '../../components/procedures/widget/dental/OdontogramSection';
import ProcedureSummary from '../../components/procedures/widget/ProcedureSummary';
import QuickSearchAdd from '../../components/procedures/widget/QuickSearchAdd';
import SavedCombosPicker from '../../components/procedures/widget/SavedCombosPicker';
import SaveAsCombo from '../../components/procedures/widget/SaveAsCombo';
import TotalsRibbon from '../../components/procedures/widget/TotalsRibbon';
import ShrinkCoachmark from '../../components/procedures/widget/ShrinkCoachmark';
import ExpandedProcedureCanvas from '../../components/procedures/widget/ExpandedProcedureCanvas';
import { formatToken } from '../../utils/formatToken';
import { Z_WIDGET_RAIL } from '../../constants/zLayers';
import useRightEdgePanelStore from '../../stores/rightEdgePanelStore';

/**
 * ProcedureWidget — the always-mounted procedure cockpit, pinned to the
 * right edge of the visit screen. Migrated from the legacy
 * `ProcedureHalfDiscWidget`; behaviour is preserved verbatim except:
 *   - rail-size state now uses `useRailState` (namespaced storage key
 *     per `(visitId, kind)` so future rail-mounted widgets don't stomp
 *     each other);
 *   - z-index now consumes `Z_WIDGET_RAIL` / `Z_WIDGET_OVERLAY` from
 *     `src/constants/zLayers.js` instead of a hardcoded 30/1200.
 *
 * Layout strategy:
 *   - Disc renders as `position: fixed` on the right edge of the
 *     viewport with z-index above the visit form. Reliable across
 *     scroll, max-min layouts, and panel maximisation.
 *   - The visit form column adjusts its `padding-right` to leave room
 *     for the disc, so the form is never visually overlapped or cut
 *     off. Padding is the disc's current width + a small margin.
 *   - When the user clicks ⤢, a real full-screen ExpandedProcedureCanvas
 *     mounts. The disc itself collapses to zero footprint while the
 *     canvas is open.
 */
export default function ProcedureWidget({
  visit,
  viewer,
  onWidthChange,            // optional: parent uses this to add padding-right
  onCoarseShrunkTap,        // optional: coarse-pointer chip tap (handoff to chart plugin etc.); if absent, falls through to setSize('focused')
  pairedFullSize = false,   // optional: when true, a sibling pane is mounted alongside the rail; lock at 'focused', square left corners, suppress click-outside collapse and chip-tap handoff. Chart-agnostic — integrator (DoctorQueueView) decides when to set it.
}) {
  const { isDark } = useTheme();
  const visitId = visit?.visit_id;

  const byVisit = useSittingStore((s) => s.byVisit);
  const fetchForVisit = useSittingStore((s) => s.fetchForVisit);
  const fetchVisitNotes = useSittingStore((s) => s.fetchVisitNotes);
  // Doctor's per-visit half-disc shows every non-terminal procedure on
  // the visit, INCLUDING awaiting_payment, EXCEPT once the visit itself
  // is closed: at that point payment collection becomes the
  // receptionist's job, so the doctor stops seeing awaiting_payment
  // chips on this visit (they appear in the receptionist's
  // Action-Pending list instead).
  const isVisitTerminal = (
    visit?.status === 'COMPLETED'
    || visit?.status === 'CANCELLED'
  );
  const sittings = useMemo(() => {
    const all = (byVisit && visitId ? (byVisit[visitId] || []) : []);
    if (!isVisitTerminal) return all;
    return all.filter((s) => s?.status !== 'awaiting_payment');
  }, [byVisit, visitId, isVisitTerminal]);

  useEffect(() => {
    if (!visitId) return;
    if (fetchForVisit) fetchForVisit(visitId);
    // One GSI9 Query for every procedure note on the visit, so the
    // inline preview under each chip + the (N) badge work without
    // depending on any sibling panel populating notesBySitting.
    if (fetchVisitNotes) fetchVisitNotes(visitId);
  }, [visitId, fetchForVisit, fetchVisitNotes]);

  const widgetRef = useRef(null);
  const { size, setSize, expandToCanvas, collapseToDisc } = useRailState({
    visitId,
    kind: 'procedure',
  });

  // When the doctor's cross-visit "Pending Tasks" panel is open it owns
  // the right gutter; suppress this widget's collapsed disc so the teal
  // circle never paints on top of the panel and hides its cost text.
  const pendingTasksOpen = useRightEdgePanelStore((s) => s.pendingTasksOpen);

  // Phase 2 bridge — when a chart / summary / banner fires
  // `sdk.openWidget('procedure', { visitId, prefill })` with the
  // default 'rail' placement, this hook captures the prefill, expands
  // the rail to 'focused', and closes the store. Chart-pane placement
  // is skipped here (chart owns the picker mount in that branch).
  const { pendingPrefill, clearPending } = useProcedurePrefillBridge({
    visitId,
    setSize,
  });

  const reload = useCallback(() => {
    if (!visitId) return;
    if (fetchForVisit) fetchForVisit(visitId);
    if (fetchVisitNotes) fetchVisitNotes(visitId);
  }, [visitId, fetchForVisit, fetchVisitNotes]);

  const { handleAction: rawHandleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  // The half-disc is pinned to a single visit, so the queue badge
  // (Q-X · count) on the disc already names the patient. Skip the
  // start-confirmation dialog — it's redundant here. The doctor's
  // pending-task badge (RoleTaskBadge) keeps its own existing rule
  // (skip only when a Q-X pill is active) and is unaffected.
  const handleAction = useCallback(
    (event) => rawHandleAction({ ...event, skipConfirm: true }),
    [rawHandleAction],
  );

  const handleDelete = useCallback((sitting) => {
    return handleAction({ action: 'cancel', sitting });
  }, [handleAction]);

  const [view, setView] = useState('default'); // 'default' | 'combos'
  const [autoFocusSearch, setAutoFocusSearch] = useState(false);
  useEffect(() => {
    if (sittings.length > 0) setAutoFocusSearch(false);
  }, [sittings.length]);

  // Coarse-pointer (tablet/phone) handoff. The chart-plugin layer is the
  // one that knows whether to do anything with the tap — this widget is
  // chart-agnostic and just exposes the hook. If the parent supplies an
  // `onCoarseShrunkTap` handler we call it instead of expanding the rail.
  // The handler returning a truthy value short-circuits the rail-expand;
  // returning falsy (or omitted handler) means the rail expands as today.
  const isCoarse = useCoarsePointer();
  const isPortrait = usePortraitOrientation();
  // Variant M — when a sibling pane is paired with us, we lock the rail
  // at `'focused'` so the user sees pane + actions side by side. The
  // sibling pane's clicks are exempt from click-outside collapse (the
  // pane carries `.arogya-chart-pane`), and the chip-tap handoff is
  // suppressed because the sibling is already open (tapping the chip is
  // a no-op rather than reopening anything).
  const canHandoff = isCoarse
    && !pairedFullSize
    && typeof onCoarseShrunkTap === 'function';

  // Replace the focus-shrink + hover-leave-timer combo with a
  // click-outside pattern. The previous logic was collapsing the disc
  // the moment the user clicked an interior control (e.g. an
  // AssigneePicker avatar at the edge), making it impossible to add a
  // procedure. New rule: hover or click opens; only a genuine click
  // outside the disc closes it.
  //
  // Hover-to-open is a fine-pointer affordance only. Touch devices
  // don't get a real "enter" event before the click anyway, but we
  // still skip the rail-expand on coarse pointers so a hover event from
  // a docked tablet keyboard doesn't pre-empt the chairside branch.
  const handleMouseEnter = useCallback(() => {
    if (canHandoff) return;
    if (size === 'shrunk' || size === 'idle') setSize('focused');
  }, [canHandoff, size, setSize]);

  const handleShrunkTap = useCallback(() => {
    if (canHandoff) {
      const handled = onCoarseShrunkTap({ visit, viewer });
      if (handled) return;
    }
    setSize('focused');
  }, [canHandoff, onCoarseShrunkTap, visit, viewer, setSize]);

  useEffect(() => {
    if (size !== 'focused') return undefined;
    // Variant M — while a sibling pane is paired, we want the rail
    // locked open. Clicks on the paired pane (which carries
    // `role="presentation"`, not `dialog`) should NOT collapse the rail
    // either, otherwise the user loses controls the moment they touch
    // the sibling. Suppress the click-outside collapse entirely while
    // paired.
    if (pairedFullSize) return undefined;
    const onPointerDown = (e) => {
      const node = widgetRef.current;
      if (!node) return;
      if (node.contains(e.target)) return;
      const target = e.target;
      if (target?.closest?.('[role="dialog"], [data-keep-bubble-open="1"], .arogya-chart-pane')) {
        return;
      }
      setSize('shrunk');
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [size, setSize, pairedFullSize]);

  // Variant M — while a sibling pane is paired, the rail is always
  // displayed at `'focused'` so the user sees actions next to the pane.
  // We DO NOT call `setSize('focused')` (that would persist to
  // sessionStorage and stick after the pane closes); instead we override
  // the display-time `size` value used for layout. When pairing ends the
  // override drops and the rail returns to whichever size the user had
  // it at before.
  const displaySize = pairedFullSize ? 'focused' : size;
  // Tablet portrait pair — the rail no longer sits to the RIGHT of the chart;
  // it stacks BELOW it as a full-width bottom band (the chart fills the top
  // 60 %, see ToothChartContainer's portrait branch). `isStacked` drives that
  // bottom-band layout below. Landscape / desktop keep the right-edge rail.
  const isStacked = pairedFullSize && isPortrait;

  // Width per size mode. Both 'shrunk' and 'idle' render as a small
  // circular badge — the doctor only sees the count by default and
  // hovers / focuses to open the panel. When stacked the band spans the
  // full width via left/right insets, so this width is just the fallback
  // value reported to onWidthChange (which reports 0 while stacked).
  const width = useMemo(() => {
    switch (displaySize) {
      case 'focused': return 380;
      case 'canvas':  return 0;
      case 'shrunk':
      case 'idle':
      default:        return 64;
    }
  }, [displaySize]);

  useEffect(() => {
    if (typeof onWidthChange === 'function') {
      // Stacked = full-width bottom band; it reserves no right gutter.
      onWidthChange(displaySize === 'canvas' || isStacked ? 0 : width + 16);
    }
  }, [width, displaySize, isStacked, onWidthChange]);

  const count = sittings.length;
  const isShrunk = displaySize === 'shrunk' || displaySize === 'idle';
  const isCanvas = displaySize === 'canvas';

  const rawToken = formatToken(
    visit?.queue_number,
    visit?.appointment_number,
    { hash: false },
  );
  const queueToken = rawToken ? `Q-${rawToken}` : '';

  const palette = useMemo(() => ({
    bg:      isDark ? '#0f172a' : '#ffffff',
    bgAlt:   isDark ? '#1e293b' : '#f8fafc',
    border:  isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)',
    text:    isDark ? '#e2e8f0' : '#1e293b',
    muted:   isDark ? '#94a3b8' : '#64748b',
    badgeBg: isDark ? 'rgba(13,148,136,0.20)' : '#ccfbf1',
    badgeFg: isDark ? '#5eead4' : '#0f766e',
    accent:  '#0d9488',
  }), [isDark]);

  if (isCanvas) {
    return (
      <>
        <ExpandedProcedureCanvas
          visit={visit}
          viewer={viewer}
          onClose={collapseToDisc}
        />
        {dialogs}
      </>
    );
  }

  // The doctor's "Pending Tasks" panel is open and owns the right gutter —
  // hide the collapsed disc so it can't overlap the panel / occlude its
  // cost text. Keep dialogs mounted; the disc returns when the panel
  // closes (store flips back to false). Only the shrunk state is gated;
  // the expanded rail/canvas are deliberate full-screen interactions.
  if (isShrunk && pendingTasksOpen) {
    return <>{dialogs}</>;
  }

  return (
    <>
      <aside
        ref={widgetRef}
        aria-label={`Procedures (${count})`}
        data-procedure-widget-size={size}
        onMouseEnter={handleMouseEnter}
        onClick={isShrunk ? handleShrunkTap : undefined}
        style={{
          position: 'fixed',
          // Stacked (portrait paired) → full-width bottom band sitting under
          // the chart's top 60 %. Otherwise the right-edge rail (or the
          // circular badge when shrunk).
          top: isShrunk ? '50%' : (isStacked ? '60%' : '5.5rem'),
          left: isStacked && !isShrunk ? '0.5rem' : undefined,
          right: isShrunk ? '0.75rem' : (isStacked ? '0.5rem' : 0),
          bottom: isShrunk ? undefined : '1rem',
          transform: isShrunk ? 'translateY(-50%)' : undefined,
          width: isStacked && !isShrunk ? 'auto' : width,
          minWidth: isStacked && !isShrunk ? 0 : width,
          maxWidth: isStacked && !isShrunk ? 'none' : width,
          height: isShrunk ? width : undefined,
          maxHeight: isShrunk ? width : (isStacked ? 'calc(40vh - 1.5rem)' : 'calc(100vh - 6.5rem)'),
          // Z_WIDGET_RAIL keeps the rail above the chart pane (Z_CHART_PANE)
          // in PR 3 — the chart will no longer occlude the rail.
          zIndex: Z_WIDGET_RAIL,
          transition: 'width 150ms ease-out, min-width 150ms ease-out, max-width 150ms ease-out, height 150ms ease-out',
          background: isShrunk ? palette.accent : palette.bg,
          color: isShrunk ? '#ffffff' : palette.text,
          cursor: isShrunk ? 'pointer' : 'default',
          // Longhand sides only — mixing the `border` shorthand with a
          // conditional `borderRight` longhand triggers React's shorthand/
          // longhand collision warning on every re-render. Right-edge rail
          // drops its right border (meets the viewport edge); the stacked
          // bottom band keeps all four borders (it floats with a 0.5rem
          // inset on both sides).
          borderTop: isShrunk ? 'none' : `1px solid ${palette.border}`,
          borderBottom: isShrunk ? 'none' : `1px solid ${palette.border}`,
          borderLeft: isShrunk ? 'none' : `1px solid ${palette.border}`,
          borderRight: isShrunk ? 'none' : (isStacked ? `1px solid ${palette.border}` : 'none'),
          // Variant M corner squaring:
          //  • stacked band → square the TOP corners (it butts against the
          //    chart above), round the bottom.
          //  • right rail when paired → square the LEFT corners, otherwise the
          //    24×24 rounded regions expose DoctorQueueView workflow underneath
          //    (the paired pane meets the rail's left edge with no gap).
          borderTopLeftRadius: isShrunk ? '50%' : (isStacked ? 0 : (pairedFullSize ? 0 : 24)),
          borderTopRightRadius: isShrunk ? '50%' : (isStacked ? 0 : 0),
          borderBottomLeftRadius: isShrunk ? '50%' : (isStacked ? 16 : (pairedFullSize ? 0 : 24)),
          borderBottomRightRadius: isShrunk ? '50%' : (isStacked ? 16 : 0),
          boxShadow: isShrunk
            ? '0 6px 18px rgba(13,148,136,0.45), 0 2px 6px rgba(0,0,0,0.15)'
            // Right rail casts its shadow leftward (−12px); the stacked bottom
            // band casts it upward (negative Y) toward the chart above.
            : isStacked
              ? (isDark
                ? '0 -12px 28px rgba(0,0,0,0.45), 0 -2px 8px rgba(0,0,0,0.25)'
                : '0 -12px 28px rgba(15,23,42,0.12), 0 -2px 8px rgba(15,23,42,0.06)')
              : isDark
                ? '-12px 8px 28px rgba(0,0,0,0.45), -2px 2px 8px rgba(0,0,0,0.25)'
                : '-12px 8px 28px rgba(15,23,42,0.12), -2px 2px 8px rgba(15,23,42,0.06)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isShrunk ? 'center' : 'stretch',
          justifyContent: isShrunk ? 'center' : 'flex-start',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        }}
      >
        {isShrunk && (
          <div
            aria-label={
              queueToken
                ? `${count} procedure${count === 1 ? '' : 's'} on ${queueToken}. Hover to open.`
                : `${count} procedure${count === 1 ? '' : 's'}. Hover to open.`
            }
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              userSelect: 'none',
            }}
          >
            {queueToken ? (
              <span
                aria-hidden="true"
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}
              >
                {queueToken} · {count}
              </span>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: count > 99 ? '1.25rem' : '1.5rem',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {count}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    marginTop: 2,
                    fontSize: '0.5625rem',
                    opacity: 0.85,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {count === 1 ? 'PROC' : 'PROCS'}
                </span>
              </>
            )}
          </div>
        )}

        {!isShrunk && (
          <header
            style={{
              padding: '0.75rem 1rem',
              borderBottom: `1px solid ${palette.border}`,
              background: palette.bgAlt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 10,
                background: 'linear-gradient(140deg,#0d9488,#14b8a6)',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                boxShadow: '0 6px 14px -6px rgba(13,148,136,0.7)',
              }}
              title={`${count} procedure${count === 1 ? '' : 's'}`}
            >
              {count}
            </span>
            <span style={{ fontSize: '0.9375rem', fontWeight: 800, letterSpacing: '-0.01em', flex: 1 }}>
              Procedures
            </span>
            <button
              type="button"
              onClick={expandToCanvas}
              aria-label="Open full editor"
              title="Full editor"
              className="inline-flex items-center gap-1 rounded-lg border bg-white text-slate-700 hover:bg-slate-100
                         dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              style={{ borderColor: palette.border, fontSize: '0.6875rem', fontWeight: 700, lineHeight: 1, padding: '0.4rem 0.6rem' }}
            >
              <span aria-hidden="true">⤢</span> Full editor
            </button>
          </header>
        )}

        {!isShrunk && (
        <div
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            fontSize: '0.8125rem',
            color: palette.text,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
            minHeight: 0,
          }}
        >
          {/* Patient-context findings strip — sits directly under the
              header. Self-returns null for non-dentists, so the body
              starts straight at the task list for non-dental doctors. */}
          <OdontogramSection
            visit={visit}
            viewer={viewer}
            palette={palette}
            isDark={isDark}
            pendingTokens={[]}
          />

          {/* No chart for this specialty → generic Procedure Summary. It
              SELF-GATES (returns null for dentists / while specialty resolves),
              mirroring OdontogramSection, so the two are mutually exclusive and
              this widget needn't import from src/charts. */}
          <ProcedureSummary visit={visit} viewer={viewer} isDark={isDark} />

          {count === 0 && view === 'default' && (
            <button
              type="button"
              onClick={() => setAutoFocusSearch(true)}
              className="rounded-lg border border-dashed border-teal-400 bg-teal-50
                         hover:bg-teal-100 text-teal-800
                         dark:bg-teal-900/30 dark:hover:bg-teal-900/50 dark:text-teal-100
                         dark:border-teal-600"
              style={{
                padding: '0.75rem 1rem',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span aria-hidden="true">＋</span> Add first procedure
            </button>
          )}

          <ProcedureChipList
            sittings={sittings}
            viewer={viewer}
            onAction={handleAction}
            onDelete={handleDelete}
            compact={false}
            emptyHint={null}
            currentVisitDoctorMemberId={visit?.doctor_member_id || null}
          />

          {view === 'default' && (
            <>
              <QuickSearchAdd
                visit={visit}
                viewer={viewer}
                onCreated={() => { reload(); clearPending(); }}
                palette={palette}
                autoFocus={autoFocusSearch || !!pendingPrefill}
                prefilledTokens={pendingPrefill?.tokens || undefined}
                prefilledDiagnosisMetaId={pendingPrefill?.diagnosisMetaId || undefined}
                prefilledDiagnosisSummary={pendingPrefill?.diagnosisSummary || undefined}
                prefilledProcedureId={pendingPrefill?.procedureId || undefined}
                prefillRequestId={pendingPrefill?.requestId || undefined}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button
                  type="button"
                  onClick={() => setView('combos')}
                  className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md border
                             bg-white text-slate-700 hover:bg-slate-100
                             dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700
                             dark:border-slate-600"
                  style={{ fontSize: '0.6875rem', borderColor: palette.border }}
                >
                  📘 Treatment plans
                </button>
                <span style={{ fontSize: '0.5625rem', opacity: 0.6, textAlign: 'center' }}>
                  Apply a saved treatment plan
                </span>
              </div>
            </>
          )}

          {view === 'combos' && (
            <SavedCombosPicker
              visit={visit}
              viewer={viewer}
              onAttached={async () => { await reload(); setView('default'); }}
              palette={palette}
              onClose={() => setView('default')}
            />
          )}
        </div>
        )}

        {!isShrunk && view === 'default' && (
          <footer
            style={{
              padding: '0.5rem 1rem',
              borderTop: `1px solid ${palette.border}`,
              background: palette.bgAlt,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              flexShrink: 0,
            }}
          >
            <TotalsRibbon sittings={sittings} palette={palette} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <SaveAsCombo
                sittings={sittings}
                palette={palette}
                onSaved={reload}
              />
            </div>
          </footer>
        )}

        <ShrinkCoachmark size={size} palette={palette} />
      </aside>
      {dialogs}
    </>
  );
}
